/** @jsxImportSource react */
import { useState } from 'react'
import { Badge, Box, Button, Divider, IconButton, List, ListItemButton, Popover, Stack, Tooltip, Typography } from '@mui/material'
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { notificationsAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import { formatRelativeTime } from '../../utils/helpers'

/** Bell with unread count; opening it lists the latest notifications. Polls every 30s. */
const NotificationBell = () => {
  const tenantId = useTenantKey()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [anchor, setAnchor] = useState(null)
  const queryKey = [tenantId, 'notifications']
  const { data } = useQuery({ queryKey, queryFn: () => notificationsAPI.list(20), enabled: !!tenantId, refetchInterval: 30_000 })
  const refresh = () => queryClient.invalidateQueries({ queryKey })
  const readOne = useMutation({ mutationFn: notificationsAPI.read, onSuccess: refresh })
  const readAll = useMutation({ mutationFn: notificationsAPI.readAll, onSuccess: refresh })
  const unread = data?.unread || 0

  const open = (n) => {
    if (!n.read) readOne.mutate(n.id)
    setAnchor(null)
    if (n.link) navigate(n.link)
  }

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton onClick={(e) => { setAnchor(e.currentTarget); refresh() }} aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`}>
          <Badge badgeContent={unread} color="error" max={99}><NotificationsNoneOutlinedIcon /></Badge>
        </IconButton>
      </Tooltip>
      <Popover open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: { xs: 'calc(100vw - 32px)', sm: 380 }, maxHeight: 480, display: 'flex', flexDirection: 'column' } }}>
        <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1.25 }}>
          <Typography variant="subtitle1" sx={{ flexGrow: 1, fontWeight: 600 }}>Notifications</Typography>
          <Button size="small" disabled={!unread || readAll.isPending} onClick={() => readAll.mutate()}>Mark all read</Button>
        </Stack>
        <Divider />
        <Box sx={{ overflowY: 'auto' }}>
          {data?.items.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>Nothing yet. Automations can notify you here.</Typography>
          )}
          <List disablePadding>
            {data?.items.map((n) => (
              <ListItemButton key={n.id} onClick={() => open(n)} sx={{ alignItems: 'flex-start', gap: 1.25, py: 1.25 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', mt: 0.9, flexShrink: 0, bgcolor: n.read ? 'transparent' : 'primary.main' }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: n.read ? 400 : 600 }}>{n.title}</Typography>
                  {n.body && <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>{n.body}</Typography>}
                  <Typography variant="caption" color="text.disabled">{formatRelativeTime(n.createdOn)}</Typography>
                </Box>
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Popover>
    </>
  )
}

export default NotificationBell
